import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { common } from '@mui/material/colors';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Forward5Icon from '@mui/icons-material/Forward5Outlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExitOutlined';
import FullscreenIcon from '@mui/icons-material/FullscreenOutlined';
import PauseIcon from '@mui/icons-material/PauseOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import Replay5Icon from '@mui/icons-material/Replay5Outlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUpOutlined';
import type { MulticamAnnotation } from '../../graphqlService';
import { formatTimecode, stepFrames as stepFrameTime } from '../../utils/timecode';
import { studioDark } from '../AnnotationStudio/studioTokens';
import type { ReviewAngle } from './multicamReview';
import { LiveCutPlan, LivePressPlan, describeLivePress, describeLiveUndo } from './liveCutModel';
import type { LivePress } from './liveCutSession';
import { angleName, cutLabel, liveAngleKeyAt, programSegments } from './multicamStudioModel';
import { angleColor, playerTokens } from './multicamTheme';
import { useSyncedAngles } from './useSyncedAngles';

type LiveAngle = ReviewAngle & { label?: string | null };

/** The studio drives the switcher through this: keys, the timeline and mode changes. */
export interface LiveSwitcherHandle {
  /** Seconds on the shared timeline, read from the clock now. */
  getTime: () => number;
  isPlaying: () => boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  skip: (deltaSeconds: number) => void;
  stepFrames: (frames: number) => void;
  /** Cuts the program to an angle on the frame under the playhead now. */
  press: (angleKey: string) => void;
  undo: () => void;
}

interface MulticamLiveSwitcherProps {
  angles: LiveAngle[];
  audioAngleKey?: string | null;
  /** The set's separate sound recording: heard instead of the audio angle, and the clock. */
  audioSource?: ReviewAngle | null;
  annotations: MulticamAnnotation[];
  masterKey: string;
  fps: number;
  /** Seconds to open at: the review player's playhead. */
  initialTime: number;
  muted: boolean;
  volume: number;
  onToggleMute: () => void;
  /** Why cuts can't be made here (locked, or not loaded); null when they can. */
  readOnlyReason: string | null;
  onPress: (press: LivePress) => LivePressPlan;
  onUndo: () => LiveCutPlan | null;
  canUndo: boolean;
  /** The shared clock (throttled while playing), for the studio's timeline. */
  onTimeChange?: (seconds: number, playing: boolean) => void;
  /** Playback started or stopped, or the playhead jumped: a take ends. */
  onTakeBoundary?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

type FeedbackTone = 'cut' | 'same' | 'blocked' | 'undo';

interface Feedback {
  id: number;
  /** The tile that flashes; null for undo and messages about the whole set. */
  angleKey: string | null;
  tone: FeedbackTone;
  message: string;
}

/** How long a cut's message stays up. */
const MESSAGE_MS = 1800;
/** A tap's time is read when the finger lands, if the click follows within this. */
const POINTER_PRESS_MS = 1500;
/** The program preview shows this many seconds, a third of them behind the playhead. */
const PROGRAM_WINDOW_S = 30;
const SPEEDS = [0.5, 1, 1.5, 2];
const TIMECODE_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

const flash = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const FLASH_COLOR: Record<FeedbackTone, string> = {
  cut: alpha(playerTokens.live, 0.45),
  same: alpha(common.white, 0.22),
  blocked: alpha(common.black, 0.55),
  undo: 'transparent',
};

const isApplePlatform = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

/** "⌘Z" on Apple devices, "Ctrl+Z" elsewhere. */
export const undoShortcutLabel = () => (isApplePlatform() ? '⌘Z' : 'Ctrl+Z');

/** Keep keyboard focus where it was, so Space keeps controlling playback. */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

const toneOf = (plan: LivePressPlan): FeedbackTone => {
  if (plan.kind === 'blocked') return 'blocked';
  return plan.kind === 'already-live' && plan.replaced.length === 0 ? 'same' : 'cut';
};

/** A small label laid over the picture. */
const overlayLabelSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1,
  py: 0.25,
  borderRadius: 1.5,
  bgcolor: playerTokens.overlay,
  color: playerTokens.text,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.6,
} as const;

const controlSx = {
  color: studioDark.textMuted,
  '&:hover': { color: studioDark.text, bgcolor: studioDark.hover },
  '&.Mui-disabled': { color: studioDark.disabled },
  '&:focus-visible': { outline: `2px solid ${studioDark.focusRing}`, outlineOffset: 1 },
} as const;

/**
 * Live cut: every angle plays at once on one clock (the review player's sync
 * engine), and pressing 1-9 or tapping an angle cuts the program to it on the
 * frame under the playhead.
 *
 * Equal tiles keep every angle large (up to three side by side; four in a 2×2
 * grid, five or six in rows of three); the angle
 * the program shows is framed in the live colour and labelled Live, like a
 * vision mixer's tally, the angle heard is labelled Audio, and the program
 * preview below shows the cut around the playhead. Cutting only changes styles: no <video> is reloaded or moved,
 * so a cut shows on the next frame.
 *
 * The switcher owns the clock and the transport; what a press does to the
 * cuts is decided and saved by the studio (`onPress`, `onUndo`).
 */
const MulticamLiveSwitcher = forwardRef<LiveSwitcherHandle, MulticamLiveSwitcherProps>(function MulticamLiveSwitcher(
  {
    angles,
    audioAngleKey,
    audioSource,
    annotations,
    masterKey,
    fps,
    initialTime,
    muted,
    volume,
    onToggleMute,
    readOnlyReason,
    onPress,
    onUndo,
    canUndo,
    onTimeChange,
    onTakeBoundary,
    isFullscreen = false,
    onToggleFullscreen,
  },
  ref
) {
  const [speed, setSpeed] = useState(1);
  const engine = useSyncedAngles<LiveAngle>({
    angles,
    audioAngleKey,
    audioSource,
    initialTimeMs: Math.max(0, Number.isFinite(initialTime) ? initialTime : 0) * 1000,
    muted,
    volume,
    speed,
    onTimeChange: (timeMs, isPlaying) => onTimeChange?.(timeMs / 1000, isPlaying),
    onTransport: () => onTakeBoundary?.(),
  });
  const { sortedAngles, lengthMs, currentMs, playing, tiles, videoRefs, setTile } = engine;
  const now = currentMs / 1000;
  const length = lengthMs / 1000;
  const liveKey = liveAngleKeyAt(annotations, now, masterKey, fps);
  const liveAngle = sortedAngles.find((angle) => angle.angleKey === liveKey);
  // The angle heard now, which is not always the set's audio angle: where that
  // has no footage, another clip is heard (clips played one after another).
  const heardKey = engine.soundKeyAt(currentMs);

  // ---- Feedback: a flash on the tile and a short message ---------------------
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [messageShown, setMessageShown] = useState(false);
  const feedbackSeqRef = useRef(0);
  const hideTimerRef = useRef<number | null>(null);
  const show = useCallback((next: Omit<Feedback, 'id'>) => {
    feedbackSeqRef.current += 1;
    setFeedback({ ...next, id: feedbackSeqRef.current });
    setMessageShown(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setMessageShown(false), MESSAGE_MS);
  }, []);
  useEffect(() => () => {
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
  }, []);

  // ---- Cutting -----------------------------------------------------------------
  /** Where a tap landed: read at pointerdown, used by the click that follows. */
  const pointerPressRef = useRef<{ angleKey: string; time: number; at: number } | null>(null);

  const cut = (angleKey: string, at?: number) => {
    const time = typeof at === 'number' ? at : engine.getTimeMs() / 1000;
    if (readOnlyReason) {
      show({ angleKey, tone: 'blocked', message: readOnlyReason });
      return;
    }
    const plan = onPress({ angleKey, time, duration: engine.lengthMs / 1000, hasFootage: engine.covers });
    // Show the new live angle now, not at the next clock update.
    if (plan.kind !== 'blocked') engine.refresh();
    show({ angleKey, tone: toneOf(plan), message: describeLivePress(plan, sortedAngles, fps) });
  };

  const undo = () => {
    if (readOnlyReason) {
      show({ angleKey: null, tone: 'blocked', message: readOnlyReason });
      return;
    }
    const plan = onUndo();
    if (!plan) {
      show({ angleKey: null, tone: 'blocked', message: 'There’s nothing to undo.' });
      return;
    }
    engine.refresh();
    show({ angleKey: null, tone: 'undo', message: describeLiveUndo(plan, sortedAngles, fps) });
  };

  // ---- Transport -----------------------------------------------------------------
  const toggle = () => (engine.isPlaying() ? engine.pauseExact() : engine.play());
  const seekSeconds = (seconds: number) => engine.seek(Math.max(0, Number.isFinite(seconds) ? seconds : 0) * 1000);
  const skip = (deltaSeconds: number) => engine.seek(engine.getTimeMs() + deltaSeconds * 1000);
  const stepFrames = (frames: number) => {
    if (engine.isPlaying()) engine.pauseExact();
    engine.seek(stepFrameTime(engine.getTimeMs() / 1000, frames, fps, length > 0 ? length : undefined) * 1000);
  };

  useImperativeHandle(ref, () => ({
    getTime: () => engine.getTimeMs() / 1000,
    isPlaying: engine.isPlaying,
    play: engine.play,
    pause: engine.pauseExact,
    toggle,
    seek: seekSeconds,
    skip,
    stepFrames,
    press: (angleKey: string) => cut(angleKey),
    undo,
  }));

  // ---- Program preview: the cut around the playhead ---------------------------------
  const segments = useMemo(() => programSegments(annotations, masterKey, length), [annotations, masterKey, length]);
  const windowSpan = Math.min(PROGRAM_WINDOW_S, length);
  const windowStart = Math.max(0, Math.min(now - windowSpan / 3, length - windowSpan));
  const windowEnd = windowStart + windowSpan;
  const percentIn = (seconds: number) => (windowSpan > 0 ? ((seconds - windowStart) / windowSpan) * 100 : 0);
  const visibleSegments = segments.filter((segment) => segment.end > windowStart && segment.start < windowEnd);

  const count = sortedAngles.length;
  // Four make a 2×2 grid; otherwise up to three across.
  const columns = count === 4 ? 2 : Math.min(3, Math.max(2, count));
  const rowsWide = Math.ceil(count / columns);
  const rowsNarrow = Math.ceil(count / 2);
  const readOnly = Boolean(readOnlyReason);

  return (
    <Box
      data-testid="multicam-live-switcher"
      sx={{ display: 'flex', flexDirection: 'column', flex: isFullscreen ? 1 : 'none', minHeight: 0 }}
    >
      {engine.audioSource && (
        // The set's own recorder: heard and keeping time, never shown.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={engine.audioSourceRef}
          src={engine.audioSource.videoUrl}
          preload="auto"
          data-testid="live-audio-source"
          onLoadedMetadata={(event) => engine.handleAudioSourceLoadedMetadata(event.currentTarget)}
          style={{ display: 'none' }}
        />
      )}
      <Box
        sx={{
          position: 'relative',
          p: { xs: 0.75, sm: 1 },
          flex: isFullscreen ? 1 : 'none',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          role="group"
          aria-label="Angles to cut to"
          data-testid="live-multiview"
          sx={{
            display: 'grid',
            width: '100%',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(${columns}, minmax(0, 1fr))` },
            gap: { xs: 0.75, sm: 1 },
            // Full screen: as large as fits above the transport.
            ...(isFullscreen ? {
              maxWidth: {
                xs: `calc((100vh - 200px) * ${(2 * 16) / 9 / rowsNarrow})`,
                sm: `calc((100vh - 200px) * ${(columns * 16) / 9 / rowsWide})`,
              },
            } : {}),
          }}
        >
          {sortedAngles.map((angle, index) => {
            const key = angle.angleKey;
            const isLive = key === liveKey;
            const isAudio = key === heardKey;
            const covers = engine.covers(key, now);
            const tile = tiles[key] ?? 'loading';
            const flashHere = feedback && feedback.angleKey === key && feedback.tone !== 'undo' ? feedback : null;
            return (
              <Box
                key={key}
                data-testid={`live-tile-${key}`}
                data-live={isLive ? 'true' : 'false'}
                data-state={tile}
                sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: playerTokens.stage, borderRadius: 2, overflow: 'hidden' }}
              >
                {angle.videoUrl ? (
                  // Captions are not available for creator uploads; the tile's button names the angle.
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    ref={(el) => { videoRefs.current[key] = el; }}
                    src={angle.videoUrl}
                    preload="metadata"
                    playsInline
                    aria-hidden="true"
                    data-testid={`live-video-${key}`}
                    onLoadedMetadata={(event) => engine.handleLoadedMetadata(angle, event.currentTarget)}
                    onCanPlay={() => setTile(key, 'ready')}
                    onPlaying={() => setTile(key, 'ready')}
                    onSeeked={(event) => { if (event.currentTarget.readyState >= 3) setTile(key, 'ready'); }}
                    onWaiting={() => setTile(key, 'buffering')}
                    onError={() => setTile(key, 'error')}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                ) : null}

                {!angle.videoUrl ? (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: playerTokens.textMuted, textAlign: 'center', px: 2, pointerEvents: 'none' }}>
                    <Typography variant="body2">This angle has no video file</Typography>
                  </Box>
                ) : !covers && tile !== 'error' ? (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: playerTokens.overlay, color: playerTokens.text, textAlign: 'center', px: 2, pointerEvents: 'none' }}>
                    <Typography variant="body2">No footage here</Typography>
                  </Box>
                ) : tile === 'buffering' && playing ? (
                  <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 8, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Box sx={overlayLabelSx}>
                      <CircularProgress size={12} sx={{ color: playerTokens.text }} />
                      Buffering…
                    </Box>
                  </Box>
                ) : null}

                {flashHere && (
                  <Box
                    key={flashHere.id}
                    aria-hidden="true"
                    data-testid={`live-flash-${key}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      bgcolor: FLASH_COLOR[flashHere.tone],
                      opacity: 0,
                      animation: `${flash} 420ms ease-out`,
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}
                  />
                )}

                {/* Name and state, above the overlays so they stay readable (the button carries them for screen readers). */}
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute', top: 6, left: 6, right: 6, display: 'flex', alignItems: 'flex-start',
                    justifyContent: 'space-between', gap: 0.5, pointerEvents: 'none',
                  }}
                >
                  <Box sx={{ ...overlayLabelSx, minWidth: 0 }}>
                    {index < 9 && (
                      <Box
                        component="span"
                        sx={{
                          display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', justifyContent: 'center',
                          minWidth: 16, height: 16, px: 0.25, borderRadius: 0.75, border: `1px solid ${playerTokens.borderStrong}`,
                          fontSize: 10.5, fontWeight: 700, lineHeight: 1, flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: '2px', bgcolor: angleColor(key), boxShadow: `0 0 0 1px ${playerTokens.border}` }}
                    />
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {angleName(angle)}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {isLive && (
                      <Box component="span" data-testid={`live-badge-${key}`} sx={{ ...overlayLabelSx, bgcolor: playerTokens.live, fontWeight: 700 }}>
                        Live
                      </Box>
                    )}
                    {isAudio && (
                      <Box component="span" data-testid={`live-audio-${key}`} sx={overlayLabelSx}>
                        <VolumeUpIcon sx={{ fontSize: 14 }} />
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Audio</Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                <ButtonBase
                  disableRipple
                  aria-label={cutLabel(key, sortedAngles)}
                  aria-pressed={isLive}
                  aria-disabled={readOnly || undefined}
                  aria-keyshortcuts={index < 9 ? String(index + 1) : undefined}
                  data-testid={`live-cut-${key}`}
                  onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    pointerPressRef.current = { angleKey: key, time: engine.getTimeMs() / 1000, at: performance.now() };
                  }}
                  onMouseDown={keepFocus}
                  onClick={() => {
                    const landed = pointerPressRef.current;
                    pointerPressRef.current = null;
                    const fresh = landed && landed.angleKey === key && performance.now() - landed.at < POINTER_PRESS_MS;
                    cut(key, fresh && landed ? landed.time : undefined);
                  }}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 2,
                    touchAction: 'manipulation',
                    cursor: readOnly ? 'not-allowed' : 'pointer',
                    // The tally: the live angle is framed in the live colour.
                    boxShadow: isLive ? `inset 0 0 0 4px ${playerTokens.live}` : `inset 0 0 0 1px ${playerTokens.border}`,
                    '&:hover': {
                      boxShadow: isLive ? `inset 0 0 0 4px ${playerTokens.live}` : `inset 0 0 0 2px ${playerTokens.borderStrong}`,
                    },
                    // Inside the edge: the tile clips anything drawn outside it.
                    '&:focus-visible': { outline: `2px solid ${playerTokens.focus}`, outlineOffset: -3 },
                  }}
                />

                {tile === 'error' && (
                  <Box
                    role="alert"
                    sx={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center',
                      justifyContent: 'center', bgcolor: playerTokens.overlay, color: playerTokens.text, textAlign: 'center',
                      px: 2, pointerEvents: 'none',
                    }}
                  >
                    <ErrorOutlineOutlinedIcon fontSize="small" aria-hidden="true" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Couldn’t load this angle</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => engine.retryAngle(angle)}
                      sx={{
                        pointerEvents: 'auto',
                        color: playerTokens.text,
                        borderColor: playerTokens.borderStrong,
                        '&:hover': { borderColor: playerTokens.text, bgcolor: playerTokens.hover },
                        '&:focus-visible': { outline: `2px solid ${playerTokens.focus}`, outlineOffset: 2 },
                      }}
                    >
                      Try again
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* What the last press did, shown briefly and announced politely. */}
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute', left: 0, right: 0, bottom: { xs: 10, sm: 16 }, display: 'flex',
            justifyContent: 'center', px: 2, pointerEvents: 'none',
          }}
        >
          {feedback && (
            <Box
              key={feedback.id}
              component="span"
              data-testid="live-feedback"
              sx={{
                ...overlayLabelSx,
                px: 1.5,
                py: 0.5,
                fontSize: 13,
                maxWidth: '100%',
                fontVariantNumeric: 'tabular-nums',
                bgcolor: feedback.tone === 'cut' ? playerTokens.live : alpha(studioDark.surface, 0.92),
                opacity: messageShown ? 1 : 0,
                transition: 'opacity 240ms ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              {feedback.message}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ bgcolor: studioDark.surface, color: studioDark.text, px: { xs: 1, sm: 1.5 }, pt: 1, pb: 0.75 }} data-testid="live-transport">
        {visibleSegments.length > 0 && (
          <Box aria-hidden="true" data-testid="live-program-preview" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.75 }}>
            <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: studioDark.textSubtle }}>
              Program
            </Typography>
            <Box sx={{ position: 'relative', flex: 1, height: 16, borderRadius: 1, overflow: 'hidden', bgcolor: studioDark.rail }}>
              {visibleSegments.map((segment) => {
                const left = Math.max(0, percentIn(segment.start));
                const width = Math.min(100, percentIn(segment.end)) - left;
                return (
                  <Box
                    key={segment.id}
                    sx={{
                      position: 'absolute', top: 0, bottom: 0, left: `${left}%`, width: `${width}%`,
                      bgcolor: angleColor(segment.angleKey),
                      borderLeft: segment.cutId && segment.start >= windowStart ? `2px solid ${studioDark.surface}` : 0,
                      color: common.white, fontSize: 10, fontWeight: 700, lineHeight: '16px', pl: 0.5,
                      overflow: 'hidden', whiteSpace: 'nowrap',
                    }}
                  >
                    {width > 4 ? segment.angleKey : ''}
                  </Box>
                );
              })}
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, width: 2, left: `calc(${percentIn(now)}% - 1px)`, bgcolor: studioDark.text }} />
            </Box>
          </Box>
        )}

        <Box sx={{ px: 0.75 }}>
          <Slider
            aria-label="Seek"
            size="small"
            min={0}
            max={Math.max(lengthMs, 1)}
            step={1000 / Math.max(1, fps)}
            value={Math.min(currentMs, lengthMs > 0 ? lengthMs : currentMs)}
            disabled={lengthMs <= 0}
            onChange={(_, value) => engine.seek(value as number)}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatTimecode(value / 1000, fps)}
            sx={{
              py: '6px !important',
              color: studioDark.accent,
              height: 4,
              '& .MuiSlider-rail': { bgcolor: studioDark.rail, opacity: 1 },
              '& .MuiSlider-track': { border: 0 },
              '& .MuiSlider-thumb': { width: 12, height: 12, bgcolor: studioDark.text, '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 6px ${studioDark.accentHalo}` } },
              '& .MuiSlider-valueLabel': { fontVariantNumeric: 'tabular-nums', fontFamily: TIMECODE_FONT, fontSize: 11, bgcolor: studioDark.raised, color: studioDark.text },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: { xs: 0.25, sm: 0.5 }, rowGap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Back 5s (←)">
            <IconButton size="small" aria-label="Back 5 seconds" onMouseDown={keepFocus} onClick={() => skip(-5)} sx={controlSx}>
              <Replay5Icon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={playing ? 'Pause (Space)' : 'Play (Space)'}>
            <IconButton
              aria-label={playing ? 'Pause' : 'Play'}
              onMouseDown={keepFocus}
              onClick={toggle}
              sx={{
                mx: 0.25,
                width: 38,
                height: 38,
                bgcolor: studioDark.buttonFill,
                color: studioDark.buttonText,
                '&:hover': { bgcolor: studioDark.buttonFillHover },
                '&:focus-visible': { outline: `2px solid ${studioDark.accent}`, outlineOffset: 2 },
              }}
            >
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Forward 5s (→)">
            <IconButton size="small" aria-label="Forward 5 seconds" onMouseDown={keepFocus} onClick={() => skip(5)} sx={controlSx}>
              <Forward5Icon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, ml: 1, minWidth: 0 }}>
            <Typography
              component="span"
              data-testid="live-timecode"
              sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', fontSize: 14, fontWeight: 600, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}
            >
              {formatTimecode(now, fps)}
            </Typography>
            <Typography
              component="span"
              sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', fontSize: 12, color: studioDark.textSubtle, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline' } }}
            >
              / {formatTimecode(length, fps)}
            </Typography>
          </Box>

          {liveAngle && (
            <Box
              data-testid="live-program-label"
              sx={{ display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', gap: 0.75, ml: 1.5, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: studioDark.textMuted }}
            >
              <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: playerTokens.live, flexShrink: 0 }} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Live: {angleName(liveAngle)}
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {!readOnly && (
            <Tooltip title={`Undo the last live change (${undoShortcutLabel()})`}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UndoIcon fontSize="small" />}
                  onMouseDown={keepFocus}
                  onClick={undo}
                  disabled={!canUndo}
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    color: studioDark.text,
                    borderColor: studioDark.rail,
                    '&:hover': { borderColor: studioDark.text, bgcolor: studioDark.hover },
                    '&.Mui-disabled': { color: studioDark.disabled, borderColor: studioDark.hover },
                    '&:focus-visible': { outline: `2px solid ${studioDark.focusRing}`, outlineOffset: 2 },
                  }}
                >
                  Undo
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Playback speed">
            <Button
              size="small"
              aria-label={`Playback speed ${speed}x`}
              onMouseDown={keepFocus}
              onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
              sx={{ ...controlSx, minWidth: 44, fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
            >
              {speed}×
            </Button>
          </Tooltip>
          <Tooltip title={muted ? 'Unmute (M)' : 'Mute (M)'}>
            <IconButton size="small" aria-label={muted ? 'Unmute' : 'Mute'} onMouseDown={keepFocus} onClick={onToggleMute} sx={controlSx}>
              {muted || volume === 0 ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {onToggleFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit full screen (F)' : 'Full screen (F)'}>
              <IconButton size="small" aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} onMouseDown={keepFocus} onClick={onToggleFullscreen} sx={controlSx}>
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default MulticamLiveSwitcher;
